import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface ClientPayment {
  id: string;
  owner_id: string;
  client_id: string | null;
  appointment_id: string | null;
  session_id: string | null;
  client_name: string;
  description: string;
  amount: number;
  method: string;
  status: string;
  payment_date: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export type PaymentMethod = 'cash' | 'card' | 'transfer' | 'check' | 'other';
export type PaymentStatus = 'paid' | 'pending' | 'refunded' | 'partial';

export const PAYMENT_METHODS: { value: PaymentMethod; label: Record<'fr' | 'en' | 'es', string> }[] = [
  { value: 'cash',     label: { fr: 'Especes',        en: 'Cash',     es: 'Efectivo'      } },
  { value: 'card',     label: { fr: 'Carte bancaire', en: 'Card',     es: 'Tarjeta'       } },
  { value: 'transfer', label: { fr: 'Virement',       en: 'Transfer', es: 'Transferencia' } },
  { value: 'check',    label: { fr: 'Cheque',         en: 'Check',    es: 'Cheque'        } },
  { value: 'other',    label: { fr: 'Autre',          en: 'Other',    es: 'Otro'          } },
];

export const PAYMENT_STATUSES: { value: PaymentStatus; label: Record<'fr' | 'en' | 'es', string> }[] = [
  { value: 'paid',     label: { fr: 'Paye',      en: 'Paid',     es: 'Pagado'     } },
  { value: 'pending',  label: { fr: 'En attente', en: 'Pending',  es: 'Pendiente'  } },
  { value: 'partial',  label: { fr: 'Partiel',   en: 'Partial',  es: 'Parcial'    } },
  { value: 'refunded', label: { fr: 'Rembourse', en: 'Refunded', es: 'Reembolsado'} },
];

export function usePayments() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['client_payments', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('client_payments')
        .select('*')
        .eq('owner_id', user!.id)
        .order('payment_date', { ascending: false });
      if (error) throw error;
      return (data || []) as ClientPayment[];
    },
    enabled: !!user,
  });
}

export function usePaymentStats() {
  const { data: payments = [] } = usePayments();
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const monthPayments = payments.filter((p) => p.payment_date.startsWith(currentMonth));
  const totalMonth = monthPayments
    .filter((p) => p.status === 'paid')
    .reduce((s, p) => s + Number(p.amount), 0);
  const pendingTotal = payments
    .filter((p) => p.status === 'pending')
    .reduce((s, p) => s + Number(p.amount), 0);
  const pendingCount = payments.filter((p) => p.status === 'pending').length;

  return { totalMonth, pendingTotal, pendingCount, monthPayments };
}

export function useSavePayment() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      payment: Partial<ClientPayment> & { client_name: string; amount: number },
    ) => {
      const row = {
        client_name: payment.client_name,
        description: payment.description || '',
        amount: payment.amount,
        method: payment.method || 'cash',
        status: payment.status || 'paid',
        payment_date:
          payment.payment_date || new Date().toISOString().split('T')[0],
        notes: payment.notes || '',
        client_id: payment.client_id || null,
        appointment_id: payment.appointment_id || null,
        session_id: payment.session_id || null,
      };

      if (payment.id) {
        const { error } = await (supabase as any)
          .from('client_payments')
          .update(row)
          .eq('id', payment.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any)
          .from('client_payments')
          .insert({ ...row, owner_id: user!.id });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client_payments'] }),
  });
}

export function useDeletePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('client_payments')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client_payments'] }),
  });
}
